import { UserCheck } from "lucide-react";
import { OverviewItem } from "./helper";
import { OptionNameValuePair } from "../../../lib/interfaces/types";
import { useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";

import {
  fetchDealingOfficers,
  fetchForwardingAuthorities,
  fetchIOAppointingAuthorities,
  fetchUsersWithUserIds,
} from "../../users/logic/thunk";

import {
  getUserById,
  getForwardingAuthorities,
  getDealingOfficers,
  getIOAppointingAuthorities,
  getInvestigators,
} from "../../users/logic/slice";

import {
  addParticipant,
  updateParticipant,
} from "../logic/thunk";

import { updateCaseFields } from "../logic/validations";
import { sortOptions } from "../../../lib/utils";
import { Permission, useAbility } from "../../auth/logic/rbac";
import { selectParticipantByType } from "../logic/ParticipantsSlice";
import { useParams } from "react-router-dom";
import { ParticipantType } from "../../../../src/api/participants";
import { showErrorToast } from "../../../../src/lib/toast";

interface ParticipantItemProps {
  type: ParticipantType;
}

export const labelMap: Record<ParticipantType, string> = {
  [ParticipantType.FORWARDING_AUTHORITY]: "Forwarding Authority",
  [ParticipantType.CASE_DEALING_OFFICER]: "Dealing Officer",
  [ParticipantType.IO_APPOINTING_AUTHORITY]: "IO Appointing Authority",
  [ParticipantType.INVESTIGATOR]: "Investigating Officer",
  [ParticipantType.CASE_INITIATOR]: "Case Creator",
};

const fieldMap: Record<ParticipantType, string> = {
  [ParticipantType.FORWARDING_AUTHORITY]: updateCaseFields.FORWARDING_AUTHORITY,
  [ParticipantType.CASE_DEALING_OFFICER]: updateCaseFields.DEALING_OFFICER,
  [ParticipantType.IO_APPOINTING_AUTHORITY]: updateCaseFields.IO_APP_AUTHORITY,
  [ParticipantType.INVESTIGATOR]: updateCaseFields.INVESTIGATOR,
  [ParticipantType.CASE_INITIATOR]: "",
};

export const ParticipantItem: React.FC<ParticipantItemProps> = ({ type }) => {
  const dispatch = useAppDispatch();
  const { can } = useAbility();

  const { id: caseId } = useParams<{ id: string }>();

  const participant = useAppSelector((state) =>selectParticipantByType(state, type));

  const userId = participant?.participant_id;

  const user = useAppSelector((state: RootState) =>
    userId ? getUserById(state, userId) : undefined
  );

  useEffect(() => {
    if (userId && !user) {
      dispatch(fetchUsersWithUserIds([userId]));
    }
  }, [userId, dispatch, user]);

  const label = labelMap[type];

  const isEditable = useMemo(() => {
    if (type === ParticipantType.CASE_INITIATOR) return false;
    return can(Permission.EDIT_PARTICIPANT);
  }, [type, can]);

  const field = fieldMap[type];

  const users = useAppSelector((state: RootState) => {
    switch (type) {
      case ParticipantType.FORWARDING_AUTHORITY:
        return getForwardingAuthorities(state);
      case ParticipantType.CASE_DEALING_OFFICER:
        return getDealingOfficers(state);
      case ParticipantType.IO_APPOINTING_AUTHORITY:
        return getIOAppointingAuthorities(state);
      case ParticipantType.INVESTIGATOR:
        return getInvestigators(state);
      default:
        return [];
    }
  });

  const options: OptionNameValuePair<string>[] = useMemo(() => {
    return sortOptions(
      users.map((u) => ({
        name: u?.user_name,
        value: u?.user_id,
      }))
    );
  }, [users]);

  useEffect(() => {
    switch (type) {
      case ParticipantType.FORWARDING_AUTHORITY:
        if (!users.length) {
          dispatch(fetchForwardingAuthorities());
        }
        break;

      case ParticipantType.CASE_DEALING_OFFICER:
        if (!users.length) {
          dispatch(fetchDealingOfficers());
        }
        break;

      case ParticipantType.IO_APPOINTING_AUTHORITY:
        if (!users.length) {
          dispatch(fetchIOAppointingAuthorities());
        }
        break;
    }
  }, [type, users.length, dispatch]);


  const onSave = async (value: string) => {

    if(!caseId){
      return true;
    }

    let result;

    if (participant) {
      // UPDATE
     result= await dispatch(
        updateParticipant({
          caseId: caseId,
          participationId: participant.participation_id,
          request: {
            participant_id: value,
          },
        })
      );
    } else {
      // ADD
      result = await dispatch(
        addParticipant({
          caseId: caseId,
          request: {
            participant_type: type,
            participant_id: value,
          },
        })
      );
    }

    if (updateParticipant.fulfilled.match(result) || addParticipant.fulfilled.match(result)){
        return true;
    } else {
      showErrorToast("Failed to update. Try again.");
      return false;
    }
  };

  const displayValue = user?.user_name || "--";
  const inputType = type === ParticipantType.CASE_INITIATOR ? "text" : "select";

  return (
    <OverviewItem
      icon={<UserCheck />}
      label={label}
      isVertical
      type={inputType}
      value={user?.user_id}
      displayValue={displayValue}
      selectOptions={options}
      isEditable={isEditable}
      field={field}
      onSave={onSave}
    />
  );
};